<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  filters: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  options: { type: Array, default: () => [] },
});

const emit = defineEmits(['apply', 'search']);
const { t } = useI18n();
const input = ref('');
const datalistId = 'post-sale-address-options';
let timer;

const displayValue = filters =>
  [filters?.address_street, filters?.address_number].filter(Boolean).join(', ');

watch(
  () => props.filters,
  value => {
    input.value = displayValue(value);
  },
  { deep: true, immediate: true }
);

const scheduleSearch = () => {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => emit('search', input.value.trim()), 1000);
};

const commit = () => {
  window.clearTimeout(timer);
  const value = input.value.trim();
  const option = props.options.find(item => item.label === value);
  emit('apply', {
    address_street: option?.street || value,
    address_number: option?.number || '',
    address_unit: option?.unit || '',
  });
  emit('search', '');
};

const clear = () => {
  window.clearTimeout(timer);
  input.value = '';
  emit('apply', {
    address_street: '',
    address_number: '',
    address_unit: '',
  });
  emit('search', '');
};

onBeforeUnmount(() => window.clearTimeout(timer));
</script>

<template>
  <div class="relative w-full">
    <label class="sr-only" :for="datalistId">
      {{ t('POST_SALE.ADDRESS_FILTER.LABEL') }}
    </label>
    <span
      class="pointer-events-none absolute left-[10px] top-1/2 size-5 -translate-y-1/2 text-[#01988C]"
      :class="
        loading ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-search'
      "
      aria-hidden="true"
    />
    <input
      :id="datalistId"
      v-model="input"
      :list="`${datalistId}-list`"
      type="search"
      autocomplete="off"
      :disabled="loading"
      :placeholder="t('POST_SALE.ADDRESS_FILTER.PLACEHOLDER')"
      class="!h-14 !w-full !rounded-[12px] border border-[#E0E7EB] !bg-white !pl-[39px] !pr-24 font-[Roboto] !text-base !leading-[23px] text-[#000000DE] placeholder:text-[#A7B7BE] focus:border-[#01988C] focus:outline-none"
      @change="commit"
      @input="scheduleSearch"
      @keyup.enter.prevent="commit"
    />
    <datalist :id="`${datalistId}-list`">
      <option
        v-for="option in options"
        :key="option.id"
        :value="option.label"
      />
    </datalist>
    <button
      v-if="input"
      type="button"
      class="absolute right-2 top-1/2 -translate-y-1/2 rounded-lais px-2 py-1 text-xs font-semibold text-[#01988C] hover:bg-[#E9F7F6]"
      @click="clear"
    >
      {{ t('POST_SALE.ADDRESS_FILTER.CLEAR') }}
    </button>
  </div>
</template>
